import { ButtonRound, Flexer, Modal, SegmentedButton } from '@ontruck/orc';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { compose, setStatic, withHandlers, withProps } from 'recompose';

import {
  StyledDateCalendar,
  StyledDateCloseButton,
  StyledDatePlaceholder,
  StyledDateWrapper,
} from './StyledDateFilter';

import { StyledFilter } from '../styled';

import includeAnalytics from 'redux/modules/analytics/HOC';

import { injectIntl } from 'react-intl';
import { getTotalsByDate } from 'redux/modules/journeys/selectors';

const defaultRenderDay = (day) => <span>{day.getDate()}</span>;

const getGroupCount = (totalsByDate) => (day) => {
  return totalsByDate[moment(day).format('YYYY-MM-DD')] || 0;
};

const calendarRenderDay = (totalsByDate) => (day) => {
  const dayCount = getGroupCount(totalsByDate)(day);
  return (
    <Fragment>
      <span>{day.getDate()}</span>
      {dayCount ? <span className="DayPicker-Day-tag">{dayCount}</span> : null}
    </Fragment>
  );
};

const enhance = compose(
  setStatic('filterName', 'DateFilter'),
  connect((state, ownProps) => {
    if (ownProps.totals) {
      return {
        totalsByDate: ownProps.totals,
      };
    }

    const { filters } = ownProps;
    return {
      totalsByDate: getTotalsByDate(state, filters),
    };
  }),
  includeAnalytics,
  withHandlers(() => ({
    analyticsEvent:
      ({ sendEvent, events }) =>
      (value) => {
        sendEvent(events.FILTER, {
          type: 'date',
          value: value,
          path: window.location.pathname,
        });
      },
  })),
  withProps(({ totalsByDate }) => {
    const todayString = moment().format('YYYY-MM-DD');
    const tomorrowString = moment().add(1, 'day').format('YYYY-MM-DD');

    const countersOther = Object.keys(totalsByDate)
      .filter((k) => ![todayString, tomorrowString].includes(k))
      .reduce((acc, key) => acc + totalsByDate[key], 0);

    return {
      getGroupCount,
      todayString,
      tomorrowString,
      countersToday: totalsByDate[todayString],
      countersTomorrow: totalsByDate[tomorrowString],
      countersOther,
      calendarRenderDay: calendarRenderDay(totalsByDate),
      calendarModifiers: {
        dayWithJourneys: getGroupCount(totalsByDate),
      },
    };
  })
);

class DateFilter extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.any,
    intl: PropTypes.any,
    showTomorrow: PropTypes.bool,
    showAll: PropTypes.bool,
    calendarModifiers: PropTypes.object,
    calendarRenderDay: PropTypes.func,
    defaultValue: PropTypes.string,
    todayChildren: PropTypes.any,
    tomorrowChildren: PropTypes.any,
    stylingProps: PropTypes.object,
    analyticsEvent: PropTypes.func,
    countersToday: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    todayString: PropTypes.string,
    tomorrowString: PropTypes.string,
    countersTomorrow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    countersOther: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  static defaultProps = {
    showAll: true,
    showTomorrow: true,
    stylingProps: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      showDateModal: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleDatePickerClose = this.handleDatePickerClose.bind(this);
    this.handleDatePickerChange = this.handleDatePickerChange.bind(this);
    this.renderAllButton = this.renderAllButton.bind(this);
    this.renderTodayButton = this.renderTodayButton.bind(this);
    this.renderTomorrowButton = this.renderTomorrowButton.bind(this);
    this.renderOtherDay = this.renderOtherDay.bind(this);
    this.renderOtherPlaceholder = this.renderOtherPlaceholder.bind(this);
    this.getActiveDate = this.getActiveDate.bind(this);

    this.initialMonth = moment().toDate();
  }

  handleChange(_, { value: date }) {
    const { onChange } = this.props;
    if (date === 'other') {
      return;
    }
    onChange(date);
  }

  handleDatePickerClose() {
    this.setState({
      showDateModal: false,
    });
  }

  handleDatePickerChange(date, { disabled }) {
    if (disabled) {
      return;
    }

    this.handleChange(null, {
      value: moment(date).format('YYYY-MM-DD'),
    });

    this.setState({ showDateModal: false });
  }

  renderAllButton() {
    const { showAll, analyticsEvent } = this.props;
    const t = this.props.intl.messages.modules.Filters.date_filter;
    return showAll ? (
      <SegmentedButton.Child
        value={''}
        label={t.date_filter_all}
        onChange={() => analyticsEvent(t.date_filter_all)}
        dataE2E="filters__date_all"
      />
    ) : null;
  }

  renderTodayButton() {
    const { countersToday, todayString, todayChildren, analyticsEvent } = this.props;
    const t = this.props.intl.messages.modules.Filters.date_filter;

    return (
      <SegmentedButton.Child
        onChange={() => analyticsEvent(t.date_filter_today)}
        value={todayString}
        label={
          <Fragment>
            {t.date_filter_today}
            {!!countersToday && ` (${countersToday})`}
            {todayChildren}
          </Fragment>
        }
        dataE2E="filters__date_today"
      />
    );
  }

  renderTomorrowButton() {
    const { showTomorrow, tomorrowString, countersTomorrow, tomorrowChildren, analyticsEvent } =
      this.props;
    const t = this.props.intl.messages.modules.Filters.date_filter;

    return !!showTomorrow ? (
      <SegmentedButton.Child
        onChange={() => analyticsEvent(t.date_filter_tomorrow)}
        value={tomorrowString}
        label={
          <Fragment>
            {t.date_filter_tomorrow}
            {!!countersTomorrow && ` (${countersTomorrow})`}
            {tomorrowChildren}
          </Fragment>
        }
        dataE2E="filters__date_tomorrow"
      />
    ) : null;
  }

  renderOtherDay() {
    const { countersOther, analyticsEvent } = this.props;
    const t = this.props.intl.messages.modules.Filters.date_filter;

    return (
      <SegmentedButton.Child
        value="other"
        onChange={() => {
          this.setState({ showDateModal: true });
          analyticsEvent(t.date_filter_other);
        }}
        label={
          <Fragment>
            {t.date_filter_other}
            {!!countersOther && ` (${countersOther})`}
          </Fragment>
        }
        dataE2E="filters__date_other"
      />
    );
  }

  renderOtherPlaceholder({ activeValue }) {
    const { value, defaultValue = undefined } = this.props;

    return activeValue === 'other' ? (
      <StyledDatePlaceholder>
        <Flexer.Child>{moment(value).format('dddd DD MMMM YYYY')}</Flexer.Child>
        <StyledDateCloseButton>
          <ButtonRound
            skin="primary"
            size="XXS"
            onClick={() => this.handleChange(null, { value: defaultValue })}
            icon="close"
          />
        </StyledDateCloseButton>
      </StyledDatePlaceholder>
    ) : null;
  }

  getActiveDate() {
    const { value, showTomorrow } = this.props;

    const date = value;

    const today = moment();
    const tomorrow = moment().add(1, 'day');
    const dates = ['', 'today', 'tomorrow', 'yesterday'];

    if (dates.indexOf(date) !== -1) {
      return date;
    } else if (moment(date).isSame(today, 'day')) {
      return moment(date).format('YYYY-MM-DD');
    } else if (showTomorrow && moment(date).isSame(tomorrow, 'day')) {
      return moment(date).format('YYYY-MM-DD');
    } else {
      return 'other';
    }
  }

  render() {
    const {
      intl,
      calendarModifiers = {},
      calendarRenderDay = defaultRenderDay,
      stylingProps,
      className,
    } = this.props;

    const activeValue = this.getActiveDate();
    const t = this.props.intl.messages.modules.Filters.date_filter;
    return (
      <StyledFilter {...stylingProps} className={className}>
        <StyledDateWrapper>
          <SegmentedButton.Parent
            size="M"
            value={activeValue}
            onChange={this.handleChange}
            dataE2E="filters__date"
          >
            {this.renderAllButton()}
            {this.renderTodayButton()}
            {this.renderTomorrowButton()}
            {this.renderOtherDay()}
          </SegmentedButton.Parent>

          {this.renderOtherPlaceholder({ activeValue })}

          {this.state.showDateModal ? (
            <Modal
              show
              withPadding={false}
              headerComponent={t.select_date}
              onClose={this.handleDatePickerClose}
              size="M"
            >
              <Flexer.Parent p="S" justifyContent="center">
                <StyledDateCalendar
                  onDayClick={this.handleDatePickerChange}
                  locale={intl.locale}
                  modifiers={calendarModifiers}
                  renderDay={calendarRenderDay}
                />
              </Flexer.Parent>
            </Modal>
          ) : null}
        </StyledDateWrapper>
      </StyledFilter>
    );
  }
}

export default injectIntl(enhance(DateFilter));
